"use client"

import React, { useEffect, useState } from "react"
import Image from "next/image"
import { useRouter, useSearchParams } from "next/navigation"
import { createOrderFromCurrentBasketAction, updateOrderAfterRedirectAction } from "@/actions/basketActions"
import {
  responsivePbVariants,
  responsivePlVariants,
  responsivePrVariants,
  responsivePtVariants,
} from "@/constants/tailwindClasses"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { LoaderCircle } from "lucide-react"
import { useTranslations } from "next-intl"

import { StopActionWorkflow } from "@/lib/intershop/interfaces/order/order"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import {
  BONUS_PROGRAM_CHECKOUT_INTENT_CONSUMED_SESSION_STORAGE_KEY,
  BONUS_PROGRAM_CHECKOUT_INTENT_SESSION_STORAGE_KEY,
} from "@/lib/storefront/account/bonus-program-checkout"
import { shouldHideBonusProgramEligibility } from "@/lib/storefront/account/bonus-program-eligibility"
import type { BonusProgramEligibility, BonusProgramStatus } from "@/lib/storefront/account/bonus-program-service"
import { CheckoutContext } from "@/lib/storefront/checkout/checkout-service"
import { logTrackingFromServerResponse } from "@/lib/tracking/tracking-logger"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/components"
import BonusProgramConsentDescription from "@/components/account/BonusProgramConsentDescription"
import { UbCheckoutReviewCreateOrderButtonStoryblok } from "@/components/storyblok/component-types"

interface CheckoutReviewCreateOrderButtonProps {
  checkoutContext: CheckoutContext
  blok: UbCheckoutReviewCreateOrderButtonStoryblok
  bonusProgramEligibility?: BonusProgramEligibility
  bonusProgramStatus?: BonusProgramStatus
}

export default function CheckoutReviewCreateOrderButtonClient(props: CheckoutReviewCreateOrderButtonProps) {
  const t = useTranslations("storefront")
  const router = useRouter()
  const searchParams = useSearchParams()
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [isPending, setIsPending] = useState(false)
  const isB2c = !props.checkoutContext.isB2B
  const navisionId = props.checkoutContext.customerData?.navisionId
  const [isAdditionalMessageLoaded, setIsAdditionalMessageLoaded] = useState(false)
  const hideBonusProgram = shouldHideBonusProgramEligibility(props.bonusProgramEligibility)
  const showBonusProgramRegistration =
    props.checkoutContext.isB2B &&
    !hideBonusProgram &&
    props.bonusProgramEligibility?.eligible === true &&
    props.bonusProgramStatus?.active !== true &&
    props.bonusProgramStatus?.previouslyActive !== true
  const [registerForBonusProgram, setRegisterForBonusProgram] = useState(false)

  const { customOrderId, basketData, messageToMerchant, setMessageToMerchant, setCustomOrderId, setCreatedOrder } =
    useUbStorefrontState()
  const buttonVariant = props.blok.variant ?? "basketNext"
  const buttonSize = props.blok.size || "default"
  const shouldFillWrapperHeight = Boolean(props.blok.height || props.blok.minHeight)

  useEffect(() => {
    if (!isAdditionalMessageLoaded) {
      if (basketData) {
        if (basketData?.data?.attributes && basketData.data.attributes.length > 0) {
          const basketMessageToMerchant = basketData.data.attributes.find((a) => {
            return "BusinessObjectAttributes#Order_MessageToMerchant" === a.name
          })
          if (basketMessageToMerchant?.value) {
            setMessageToMerchant(basketMessageToMerchant.value as string)
          }

          const basketCustomOrderID = basketData.data.attributes.find((a) => {
            return "CustomOrderID" === a.name
          })
          if (basketCustomOrderID?.value) {
            setCustomOrderId(basketCustomOrderID.value as string)
          }
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setIsAdditionalMessageLoaded(true)
        }
      }
    }
  }, [basketData, isAdditionalMessageLoaded, setCustomOrderId, setMessageToMerchant])

  useEffect(() => {
    const redirect = searchParams.get("redirect")
    const orderId = searchParams.get("orderId")
    if (redirect && orderId) {
      const params: { [key: string]: string } = {}
      searchParams.forEach((value, key) => {
        params[key] = value
      })
      updateOrderAfterRedirectAction(orderId, params).then((value) => {
        if ("success" === redirect) {
          setCreatedOrder({ id: orderId })
          router.push(
            getLinkWithPrefixFromURLAndLocale("/basket/checkout/confirmation", props.checkoutContext.isoLocale)
          )
        } else {
          setErrorMessage(t("order.creation.status.not_completed"))
        }
      })
    }
  }, [searchParams, router, setCreatedOrder, props.checkoutContext.isoLocale, t])

  function createOrder() {
    setIsPending(true)
    if (showBonusProgramRegistration && registerForBonusProgram) {
      sessionStorage.setItem(BONUS_PROGRAM_CHECKOUT_INTENT_SESSION_STORAGE_KEY, "true")
      sessionStorage.removeItem(BONUS_PROGRAM_CHECKOUT_INTENT_CONSUMED_SESSION_STORAGE_KEY)
    } else {
      sessionStorage.removeItem(BONUS_PROGRAM_CHECKOUT_INTENT_SESSION_STORAGE_KEY)
      sessionStorage.removeItem(BONUS_PROGRAM_CHECKOUT_INTENT_CONSUMED_SESSION_STORAGE_KEY)
    }
    createOrderFromCurrentBasketAction(isB2c, customOrderId, navisionId, messageToMerchant).then((value) => {
      logTrackingFromServerResponse(value?.trackingLog)

      if (value?.result === "NOK") {
        console.log(value.message)
        setErrorMessage(t("order.creation.status.not_completed"))
        setIsPending(false)
      } else {
        const createdOrder = value?.orderData
        //console.dir(createdOrder, { depth: 10 })
        setMessageToMerchant("")
        setCustomOrderId("")
        if (createdOrder?.orderCreation?.status === "COMPLETED") {
          setCreatedOrder(createdOrder)
          router.push(
            getLinkWithPrefixFromURLAndLocale("/basket/checkout/confirmation", props.checkoutContext.isoLocale)
          )
        } else if (
          createdOrder?.orderCreation?.status === "STOPPED" &&
          (createdOrder?.orderCreation?.stopAction as StopActionWorkflow)?.exitReason === "approval_workflow"
        ) {
          // Fallback: go to approval page without id
          setCreatedOrder(createdOrder)
          router.push(getLinkWithPrefixFromURLAndLocale("/basket/checkout/approval", props.checkoutContext.isoLocale))
          setIsPending(false)
        }
      }
    })
  }

  return (
    <>
      {errorMessage && <p className={"text-xs text-red-500"}>{errorMessage}</p>}
      {showBonusProgramRegistration && (
        <div
          className={"mb-3 flex items-center justify-between gap-3 rounded-sm border border-gray-200 bg-gray-50 p-3"}
        >
          <div className={"flex items-start gap-2"}>
            <input
              id={"checkoutBonusProgramActive"}
              type={"checkbox"}
              checked={registerForBonusProgram}
              onChange={(event) => setRegisterForBonusProgram(event.target.checked)}
              disabled={isPending}
              className={"mt-1 checked:bg-primary hover:checked:bg-primary focus:ring-0 disabled:opacity-50"}
            />
            <div className={"flex flex-col gap-1 text-sm"}>
              <label htmlFor={"checkoutBonusProgramActive"} className={"font-semibold"}>
                {t("account.profile.company.bonus_program.checkbox")}
              </label>
              <BonusProgramConsentDescription />
            </div>
          </div>
          <Image
            src={"/img/Bonus_Icon.png"}
            alt={""}
            width={200}
            height={60}
            className={"ml-auto h-auto max-h-[60px] w-[200px] shrink-0 rounded-sm object-contain"}
          />
        </div>
      )}
      <Button
        onClick={createOrder}
        size={buttonSize}
        variant={buttonVariant}
        className={cn(
          "w-full",
          { "h-full": shouldFillWrapperHeight },
          responsivePlVariants(props.blok.pl),
          responsivePrVariants(props.blok.pr),
          responsivePtVariants(props.blok.pt),
          responsivePbVariants(props.blok.pb)
        )}
        disabled={isPending}
      >
        {!isPending ? (
          <>
            {" "}
            <p>{props.blok.label ?? "Kostenpflichtig bestellen"}</p>
          </>
        ) : (
          <div className={"flex flex-row gap-2"}>
            {" "}
            <p>{props.blok.label_processing ?? "Wird verarbeitet..."}</p>
            <LoaderCircle className={"animate-spin"} />
          </div>
        )}
      </Button>
    </>
  )
}
